import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GmailReconnectBanner, isGmailReconnectError } from '../components/GmailReconnectBanner';
import {
  Sparkles, RefreshCw, Check, X, Mail, FileText, ArrowLeft, Inbox, UserCheck, AlertTriangle, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function CredentialsEmailReview() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, needs_match: 0, approved: 0, rejected: 0 });
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [mailbox, setMailbox] = useState('shared');
  const [edits, setEdits] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const { user } = useAuth();
  const [reconnect, setReconnect] = useState({ shared: null, personal: null });

  const loadMailboxStatus = useCallback(async () => {
    const [sh, pe] = await Promise.all([
      axios.get(`${API}/communications/connection-status`, authHeaders()).catch(() => ({ data: null })),
      axios.get(`${API}/communications/personal/connection-status`, authHeaders()).catch(() => ({ data: null })),
    ]);
    setReconnect({
      shared: sh.data?.gmail_status === 'reconnect_required' ? sh.data.email : null,
      personal: pe.data?.gmail_status === 'reconnect_required' ? pe.data.email : null,
    });
  }, []);

  useEffect(() => { loadMailboxStatus(); }, [loadMailboxStatus]);

  const handleGmailError = (e, fallback) => {
    if (isGmailReconnectError(e)) {
      toast.error('Gmail credentials expired — reconnect the mailbox to scan.', { id: 'gmail-reconnect' });
      loadMailboxStatus();
      return true;
    }
    return false;
  };

  const handleDiagnose = async () => {
    setDiagLoading(true);
    try {
      const res = await axios.get(`${API}/credentials/email/diagnose`, authHeaders());
      setDiag(res.data);
    } catch (e) {
      if (handleGmailError(e)) return;
      toast.error(e?.response?.data?.detail || 'Diagnostic failed (is the shared mailbox connected?)');
    } finally {
      setDiagLoading(false);
    }
  };

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [pend, needs, c, ppl] = await Promise.all([
        axios.get(`${API}/credentials/email/queue?status=pending`, authHeaders()),
        axios.get(`${API}/credentials/email/queue?status=needs_match`, authHeaders()),
        axios.get(`${API}/credentials/email/queue/counts`, authHeaders()),
        axios.get(`${API}/personnel?status=Active`, authHeaders()),
      ]);
      const combined = [...(needs.data.items || []), ...(pend.data.items || [])];
      setItems(combined);
      setCounts(c.data || {});
      setPersonnel(ppl.data || []);
    } catch (e) {
      toast.error('Failed to load credential queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/credentials/email/scan`, { mailbox, max_results: 50 }, authHeaders());
      const d = res.data;
      if ((d.scanned || 0) === 0) {
        toast.warning('No emails found addressed to credentials@elitehomerehab.com in the connected mailbox. Check that the alias actually delivers INTO this mailbox (search "to:credentials@elitehomerehab.com" in Gmail) and that each email has the credential file attached.', { duration: 10000 });
      } else {
        toast.success(`Scanned ${d.scanned} emails · ${d.new_items} new credential(s) queued${d.skipped_existing ? ` · ${d.skipped_existing} already processed` : ''}`);
      }
      loadQueue();
    } catch (e) {
      if (handleGmailError(e)) return;
      const detail = e?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('not connected')) {
        toast.error('Shared mailbox is not connected. Go to Communications → connect the shared mailbox (the Google account that receives credentials@), then scan again.', { duration: 9000 });
      } else {
        toast.error(detail || 'Scan failed');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleDedupe = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/credentials/email/queue/dedupe`, {}, authHeaders());
      toast.success(`Removed ${res.data.removed} duplicate(s) · ${res.data.kept_unique} unique item(s) kept`);
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setScanning(false);
    }
  };

  const fieldVal = (item, key) => (edits[item.id]?.[key] ?? item[key] ?? '');
  const setField = (id, key, value) => setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));

  const persistEdits = async (id) => {
    const payload = edits[id];
    if (!payload || Object.keys(payload).length === 0) return;
    try { await axios.put(`${API}/credentials/email/queue/${id}`, payload, authHeaders()); } catch (e) { /* non-blocking */ }
  };

  const handleApprove = async (item) => {
    const therapistId = fieldVal(item, 'therapist_id');
    if (!therapistId) { toast.error('Assign a person before approving'); return; }
    setBusyId(item.id);
    try {
      await persistEdits(item.id);
      const res = await axios.post(`${API}/credentials/email/queue/${item.id}/approve`, {}, authHeaders());
      toast.success(`Filed to ${res.data.therapist_name}${res.data.is_active ? ' (current)' : ''}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveAll = async () => {
    const ready = items.filter(i => i.status !== 'needs_match').length;
    if (ready === 0) { toast.warning('No ready items to approve. Assign a person to "Needs match" items first.'); return; }
    if (!window.confirm(`Approve and file all ${ready} matched credential(s)?`)) return;
    setApprovingAll(true);
    try {
      const res = await axios.post(`${API}/credentials/email/queue/approve-all`, {}, authHeaders());
      const d = res.data;
      toast.success(`Filed ${d.approved} credential(s)${d.skipped ? ` · ${d.skipped} skipped` : ''}${d.needs_match_remaining ? ` · ${d.needs_match_remaining} still need a match` : ''}`);
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setApprovingAll(false);
    }
  };

  const handleReject = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/credentials/email/queue/${item.id}/reject`, {}, authHeaders());
      toast.success('Rejected');
      setItems(prev => prev.filter(i => i.id !== item.id));
      loadQueue();
    } catch (e) {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  const openFile = async (item) => {
    try {
      const res = await axios.get(`${API}/credentials/email/queue/${item.id}/file`, { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('File not available');
    }
  };

  const viewEmail = async (item) => {
    try {
      const res = await axios.get(`${API}/credentials/email/queue/${item.id}/email`, { ...authHeaders(), responseType: 'text' });
      const w = window.open('', '_blank');
      if (w) {
        // Render untrusted email HTML inside a sandboxed iframe (no scripts, opaque
        // origin) so it can never touch this app's session/localStorage.
        const srcdoc = res.data.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        w.document.open();
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Email source</title><style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style></head><body><iframe sandbox="" srcdoc="${srcdoc}"></iframe></body></html>`);
        w.document.close();
      }
    } catch (e) {
      toast.error('Could not load the email');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="credentials-email-review-page">
      <GmailReconnectBanner sharedEmail={reconnect.shared} personalEmail={reconnect.personal} isAdmin={user?.role === 'admin'} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Link to="/personnel" className="text-muted-foreground hover:text-foreground"><ArrowLeft size={18} /></Link>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="text-amber-500" size={26} /> Credentials by Email — AI Review
            </h1>
          </div>
          <p className="text-muted-foreground mt-1 ml-7">
            Automatically reads attachments emailed to <span className="font-semibold">credentials@elitehomerehab.com</span>, extracts the type & expiration, and matches the person. Review, assign if needed, then approve to file it.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="text-sm text-amber-600" data-testid="count-needs-match">Needs match: {counts.needs_match}</Badge>
          <Badge variant="outline" className="text-sm" data-testid="count-pending">Ready: {counts.pending}</Badge>
          <Badge variant="outline" className="text-sm text-green-600" data-testid="count-approved">Filed: {counts.approved}</Badge>
          <Badge variant="outline" className="text-sm text-red-500" data-testid="count-rejected">Rejected: {counts.rejected}</Badge>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground">Monitored Address</label>
            <Input value="credentials@elitehomerehab.com" readOnly disabled data-testid="cred-scan-address-input" />
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs font-medium text-muted-foreground">Mailbox</label>
            <select
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              data-testid="cred-scan-mailbox-select"
            >
              <option value="shared">Shared Admin Inbox</option>
              <option value="personal">My Work Email</option>
            </select>
          </div>
          <Button onClick={handleScan} disabled={scanning} data-testid="cred-scan-emails-btn">
            <RefreshCw size={16} className={`mr-1 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan Emails'}
          </Button>
          <Button variant="outline" onClick={loadQueue} disabled={loading} data-testid="cred-refresh-queue-btn">
            <Inbox size={16} className="mr-1" /> Refresh
          </Button>
          <Button variant="ghost" onClick={handleDiagnose} disabled={diagLoading} data-testid="cred-diagnose-btn">
            <AlertTriangle size={16} className="mr-1" /> {diagLoading ? 'Checking…' : 'Troubleshoot'}
          </Button>
          <Button variant="ghost" onClick={handleDedupe} disabled={scanning} data-testid="cred-dedupe-btn">
            <Trash2 size={16} className="mr-1" /> Clean up duplicates
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          New emails are parsed automatically every 15 minutes. Use "Scan Emails" to pull them in now. Already-processed attachments are skipped.
        </p>
        {diag && (
          <div className="mt-3 p-3 rounded-lg bg-[#F6F8FC] border text-xs space-y-2" data-testid="cred-diagnose-results">
            <div className="font-medium">Mailbox check for {diag.monitored_address}:</div>
            <ul className="space-y-0.5">
              {Object.entries(diag.query_counts || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{k}</span>
                  <span className={Number(v) > 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>{String(v)}</span>
                </li>
              ))}
            </ul>
            <div className="font-medium pt-1">Most recent messages in this mailbox:</div>
            <div className="space-y-1">
              {(diag.recent_messages || []).map((m, i) => (
                <div key={i} className="truncate">
                  <span className="font-medium">{m.subject || '(no subject)'}</span>
                  {' — to: '}<span className="font-mono">{m.to || '—'}</span>
                  {m.delivered_to ? <> · delivered-to: <span className="font-mono">{m.delivered_to}</span></> : null}
                </div>
              ))}
            </div>
            <p className="text-muted-foreground pt-1">
              If the counts are all 0 but your credential emails appear in "Most recent messages", the alias isn't in the To/Delivered-To header. If they don't appear at all, the alias isn't delivering into this connected mailbox.
            </p>
          </div>
        )}
      </Card>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">Review Queue ({items.length})</h2>
          {items.some(i => i.status !== 'needs_match') && (
            <Button
              size="sm"
              onClick={handleApproveAll}
              disabled={approvingAll}
              data-testid="cred-approve-all-btn"
            >
              <Check size={15} className="mr-1" />
              {approvingAll ? 'Approving…' : `Approve All Ready (${items.filter(i => i.status !== 'needs_match').length})`}
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground" data-testid="cred-empty-queue">
            <Mail className="mx-auto mb-2 opacity-50" size={32} />
            No credentials waiting. Email documents to credentials@elitehomerehab.com, then scan.
          </Card>
        ) : (
          items.map((item) => {
            const needsMatch = item.status === 'needs_match';
            return (
              <Card key={item.id} className={`p-4 ${needsMatch ? 'border-amber-300' : ''}`} data-testid={`cred-queue-item-${item.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate flex items-center gap-2">
                      <FileText size={15} className="shrink-0 text-muted-foreground" />
                      <button className="hover:underline truncate text-left" onClick={() => openFile(item)} data-testid={`cred-file-${item.id}`}>
                        {item.file_name || '(attachment)'}
                      </button>
                      {needsMatch
                        ? <Badge variant="outline" className="text-amber-600 shrink-0"><AlertTriangle size={12} className="mr-1" />Needs match</Badge>
                        : <Badge variant="outline" className="text-green-600 shrink-0"><UserCheck size={12} className="mr-1" />Matched</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      From {item.from_email || 'unknown'}{item.email_date ? ` · ${new Date(item.email_date).toLocaleDateString()}` : ''}
                      {item.confidence ? ` · AI confidence: ${item.confidence}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => viewEmail(item)} data-testid={`cred-view-email-${item.id}`}>
                      <Mail size={15} className="mr-1" /> View Email
                    </Button>
                    <Button size="sm" onClick={() => handleApprove(item)} disabled={busyId === item.id} data-testid={`cred-approve-${item.id}`}>
                      <Check size={15} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleReject(item)} disabled={busyId === item.id} data-testid={`cred-reject-${item.id}`}>
                      <X size={15} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Person {item.therapist_name ? `(AI read: "${item.therapist_name}")` : ''}
                    </label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={fieldVal(item, 'therapist_id')}
                      onChange={(e) => setField(item.id, 'therapist_id', e.target.value)}
                      onBlur={() => persistEdits(item.id)}
                      data-testid={`cred-person-${item.id}`}
                    >
                      <option value="">— Select person —</option>
                      {(item.matched_therapists || []).length > 0 && (
                        <optgroup label="AI suggestions">
                          {item.matched_therapists.map(m => (
                            <option key={`s-${m.id}`} value={m.id}>{m.name} ({m.match})</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="All therapists">
                        {personnel.map(p => (
                          <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Credential Type</label>
                    <Input
                      value={fieldVal(item, 'credential_type')}
                      onChange={(e) => setField(item.id, 'credential_type', e.target.value)}
                      onBlur={() => persistEdits(item.id)}
                      placeholder="—"
                      data-testid={`cred-type-${item.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Expiration Date</label>
                    <Input
                      value={fieldVal(item, 'expiration_date')}
                      onChange={(e) => setField(item.id, 'expiration_date', e.target.value)}
                      onBlur={() => persistEdits(item.id)}
                      placeholder="MM/DD/YYYY"
                      data-testid={`cred-exp-${item.id}`}
                    />
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
